/**
 * Personalize Layer Strategy Implementation
 *
 * Implements layer-specific logic for Personalize (P) layer processing.
 * Personalize layer handles user-uploaded content for personalization and customization.
 * This layer requires component selection from other layers.
 */

import {
  BaseLayerStrategy,
  LayerFieldConfig,
  ValidationResult,
} from './LayerStrategy';

export interface PersonalizeLayerMetadata {
  layerType: 'personalize';

  // Core personalization information
  personalName?: string;
  personalizationType?:
    | 'Avatar_Customization'
    | 'Style_Preference'
    | 'Behavior_Profile'
    | 'Content_Curation'
    | 'Experience_Adaptation'
    | 'Identity_Expression';
  description?: string;

  // User context
  userId?: string; // NNA user identifier
  userName?: string; // display name
  userPreferences?: string[]; // stated preferences
  demographicInfo?: {
    ageRange?: string;
    location?: string;
    interests?: string[];
  };

  // Personalization scope
  targetLayers?: string[]; // which layers this personalizes (S, L, W, M, G, C)
  adaptationLevel?: 'Minimal' | 'Moderate' | 'Extensive' | 'Complete';
  personalizationGoals?: string[]; // what this aims to achieve

  // Source assets (NNA addresses from other layers used as base)
  baseAssets?: {
    songAssets?: string[]; // G layer assets used as personalization base
    starAssets?: string[]; // S layer assets used as personalization base
    lookAssets?: string[]; // L layer assets used as personalization base
    moveAssets?: string[]; // M layer assets used as personalization base
    worldAssets?: string[]; // W layer assets used as personalization base
    compositeAssets?: string[]; // C layer assets used as personalization base
  };

  // User-uploaded content characteristics
  contentType?: 'Image' | 'Video' | 'Mixed_Media';
  uploadCount?: number; // number of files uploaded
  totalSize?: number; // total size in MB
  uploadDate?: Date;

  // Personalization features
  visualPreferences?: {
    colorPalette?: string[];
    stylePreferences?: string[];
    visualThemes?: string[];
  };

  behavioralPreferences?: {
    interactionStyle?: string;
    pacePreference?: 'Slow' | 'Medium' | 'Fast';
    complexityPreference?: 'Simple' | 'Moderate' | 'Complex';
    feedbackStyle?: string[];
  };

  // AI analysis of user content
  userStyle?: string[]; // detected style from uploads
  aestheticProfile?: string[]; // aesthetic preferences
  personalityTraits?: string[]; // inferred personality
  creativityLevel?: number; // 0-1 creativity score
  uniquenessScore?: number; // 0-1 how unique the user's style is

  // Personalization effectiveness
  engagementMetrics?: {
    usageFrequency?: number; // how often used
    satisfactionScore?: number; // 0-1 user satisfaction
    adaptationSuccess?: number; // 0-1 how well personalization works
  };

  // Privacy and consent
  privacyLevel?: 'Public' | 'Friends' | 'Private' | 'Anonymous';
  dataUsageConsent?: string[]; // what data usage user consented to
  retentionPeriod?: number; // days to retain personalization data
  anonymized?: boolean; // whether personally identifiable info is removed

  // Social and sharing
  shareable?: boolean; // can this personalization be shared
  influenceScore?: number; // 0-1 how much this influences others
  collaborativeElements?: string[]; // elements that can be collaborated on

  // Technical metadata
  deviceInfo?: string; // device used for personalization
  platform?: string; // platform (mobile, web, VR, etc.)
  versionCompatibility?: string[]; // compatible system versions

  // Performance optimization
  loadTime?: number; // milliseconds to load personalization
  cacheStrategy?: string; // how personalization is cached
  updateFrequency?: 'Real_Time' | 'Hourly' | 'Daily' | 'Weekly' | 'Manual';

  // Content moderation
  moderationStatus?: 'Pending' | 'Approved' | 'Flagged' | 'Rejected';
  contentFlags?: string[]; // any content concerns
  appropriatenessScore?: number; // 0-1 how appropriate content is
}

export class PersonalizeLayerStrategy extends BaseLayerStrategy {
  readonly layerCode = 'P';
  readonly layerName = 'Personalize';
  readonly layerDescription = 'User personalization and customization content';

  getRequiredFields(): string[] {
    return ['personalName', 'personalizationType', 'userId'];
  }

  getOptionalFields(): string[] {
    return [
      'description',
      'userName',
      'userPreferences',
      'demographicInfo',
      'targetLayers',
      'adaptationLevel',
      'personalizationGoals',
      'baseAssets',
      'contentType',
      'uploadCount',
      'totalSize',
      'uploadDate',
      'visualPreferences',
      'behavioralPreferences',
      'userStyle',
      'aestheticProfile',
      'personalityTraits',
      'creativityLevel',
      'uniquenessScore',
      'engagementMetrics',
      'privacyLevel',
      'dataUsageConsent',
      'retentionPeriod',
      'anonymized',
      'shareable',
      'influenceScore',
      'collaborativeElements',
      'deviceInfo',
      'platform',
      'versionCompatibility',
      'loadTime',
      'cacheStrategy',
      'updateFrequency',
      'moderationStatus',
      'contentFlags',
      'appropriatenessScore',
    ];
  }

  getFieldConfig(): LayerFieldConfig {
    return {
      required: this.getRequiredFields(),
      optional: this.getOptionalFields(),
      computed: [
        'userStyle',
        'aestheticProfile',
        'personalityTraits',
        'creativityLevel',
        'uniquenessScore',
        'appropriatenessScore',
      ],
      inherited: ['userPreferences', 'visualPreferences'],
    };
  }

  extractMetadataFromAI(aiResponse: any): PersonalizeLayerMetadata {
    const layerMetadata =
      aiResponse.layerMetadata || aiResponse.personalizeMetadata || {};

    return {
      layerType: 'personalize',

      // Core information
      personalName: layerMetadata.personalName || aiResponse.personalName || '',
      personalizationType: this.normalizePersonalizationType(
        layerMetadata.personalizationType || aiResponse.personalizationType
      ),
      description: layerMetadata.description || aiResponse.description || '',

      // User context
      userId: layerMetadata.userId || aiResponse.userId,
      userName: layerMetadata.userName || aiResponse.userName,
      userPreferences: this.normalizeArray(
        layerMetadata.userPreferences || aiResponse.userPreferences
      ),
      demographicInfo: this.normalizeDemographicInfo(
        layerMetadata.demographicInfo || aiResponse.demographicInfo
      ),

      // Personalization scope
      targetLayers: this.normalizeArray(
        layerMetadata.targetLayers || aiResponse.targetLayers
      ),
      adaptationLevel: this.normalizeAdaptationLevel(
        layerMetadata.adaptationLevel || aiResponse.adaptationLevel
      ),
      personalizationGoals: this.normalizeArray(
        layerMetadata.personalizationGoals || aiResponse.personalizationGoals
      ),

      // Base assets
      baseAssets: this.extractBaseAssets(
        layerMetadata.baseAssets || aiResponse.baseAssets
      ),

      // Content characteristics
      contentType: this.normalizeContentType(
        layerMetadata.contentType || aiResponse.contentType
      ),
      uploadCount: layerMetadata.uploadCount || aiResponse.uploadCount,
      totalSize: layerMetadata.totalSize || aiResponse.totalSize,
      uploadDate: this.normalizeDate(
        layerMetadata.uploadDate || aiResponse.uploadDate
      ),

      // Preferences
      visualPreferences: this.normalizeVisualPreferences(
        layerMetadata.visualPreferences || aiResponse.visualPreferences
      ),
      behavioralPreferences: this.normalizeBehavioralPreferences(
        layerMetadata.behavioralPreferences || aiResponse.behavioralPreferences
      ),

      // AI analysis
      userStyle: this.normalizeArray(
        layerMetadata.userStyle || aiResponse.userStyle
      ),
      aestheticProfile: this.normalizeArray(
        layerMetadata.aestheticProfile || aiResponse.aestheticProfile
      ),
      personalityTraits: this.normalizeArray(
        layerMetadata.personalityTraits || aiResponse.personalityTraits
      ),
      creativityLevel:
        layerMetadata.creativityLevel || aiResponse.creativityLevel,
      uniquenessScore:
        layerMetadata.uniquenessScore || aiResponse.uniquenessScore,

      // Engagement
      engagementMetrics: this.normalizeEngagementMetrics(
        layerMetadata.engagementMetrics || aiResponse.engagementMetrics
      ),

      // Privacy
      privacyLevel: this.normalizePrivacyLevel(
        layerMetadata.privacyLevel || aiResponse.privacyLevel
      ),
      dataUsageConsent: this.normalizeArray(
        layerMetadata.dataUsageConsent || aiResponse.dataUsageConsent
      ),
      retentionPeriod:
        layerMetadata.retentionPeriod || aiResponse.retentionPeriod,
      anonymized: layerMetadata.anonymized ?? aiResponse.anonymized,

      // Social
      shareable: layerMetadata.shareable ?? aiResponse.shareable,
      influenceScore: layerMetadata.influenceScore || aiResponse.influenceScore,
      collaborativeElements: this.normalizeArray(
        layerMetadata.collaborativeElements || aiResponse.collaborativeElements
      ),

      // Technical
      deviceInfo: layerMetadata.deviceInfo || aiResponse.deviceInfo,
      platform: layerMetadata.platform || aiResponse.platform,
      versionCompatibility: this.normalizeArray(
        layerMetadata.versionCompatibility || aiResponse.versionCompatibility
      ),
      loadTime: layerMetadata.loadTime || aiResponse.loadTime,
      cacheStrategy: layerMetadata.cacheStrategy || aiResponse.cacheStrategy,
      updateFrequency: this.normalizeUpdateFrequency(
        layerMetadata.updateFrequency || aiResponse.updateFrequency
      ),

      // Moderation
      moderationStatus: this.normalizeModerationStatus(
        layerMetadata.moderationStatus || aiResponse.moderationStatus
      ),
      contentFlags: this.normalizeArray(
        layerMetadata.contentFlags || aiResponse.contentFlags
      ),
      appropriatenessScore:
        layerMetadata.appropriatenessScore || aiResponse.appropriatenessScore,
    };
  }

  enhanceWithDefaults(
    metadata: PersonalizeLayerMetadata
  ): PersonalizeLayerMetadata {
    return {
      ...this.getDefaultMetadata(),
      ...metadata,
      layerType: 'personalize',

      // Ensure required fields have defaults
      personalName: metadata.personalName || 'Personal Profile',
      personalizationType:
        metadata.personalizationType || 'Experience_Adaptation',
      userId: metadata.userId || 'anonymous',

      // Normalize arrays
      userPreferences: this.normalizeArray(metadata.userPreferences),
      targetLayers: this.normalizeArray(metadata.targetLayers),
      personalizationGoals: this.normalizeArray(metadata.personalizationGoals),
      userStyle: this.normalizeArray(metadata.userStyle),
      aestheticProfile: this.normalizeArray(metadata.aestheticProfile),
      personalityTraits: this.normalizeArray(metadata.personalityTraits),
      dataUsageConsent: this.normalizeArray(metadata.dataUsageConsent),
      collaborativeElements: this.normalizeArray(
        metadata.collaborativeElements
      ),
      versionCompatibility: this.normalizeArray(metadata.versionCompatibility),
      contentFlags: this.normalizeArray(metadata.contentFlags),

      // Default values
      adaptationLevel: metadata.adaptationLevel || 'Moderate',
      privacyLevel: metadata.privacyLevel || 'Private',
      moderationStatus: metadata.moderationStatus || 'Pending',
      updateFrequency: metadata.updateFrequency || 'Daily',
      anonymized: metadata.anonymized ?? true,
      shareable: metadata.shareable ?? false,

      // Ensure object fields exist
      baseAssets: metadata.baseAssets || {},
      demographicInfo: metadata.demographicInfo || {},
      visualPreferences: metadata.visualPreferences || {},
      behavioralPreferences: metadata.behavioralPreferences || {},
      engagementMetrics: metadata.engagementMetrics || {},
    };
  }

  generateTags(
    metadata: PersonalizeLayerMetadata,
    description?: string
  ): string[] {
    const tags = new Set<string>();

    // Basic layer tags
    tags.add('personalize');
    tags.add('customization');
    tags.add('user-content');

    // Personalization type tags
    if (metadata.personalizationType) {
      tags.add(metadata.personalizationType.toLowerCase().replace(/_/g, '-'));
      switch (metadata.personalizationType) {
        case 'Avatar_Customization':
          tags.add('avatar');
          tags.add('character-customization');
          break;
        case 'Style_Preference':
          tags.add('style');
          tags.add('aesthetic');
          break;
        case 'Behavior_Profile':
          tags.add('behavior');
          tags.add('personality');
          break;
        case 'Content_Curation':
          tags.add('curation');
          tags.add('recommendations');
          break;
        case 'Experience_Adaptation':
          tags.add('adaptive');
          tags.add('responsive');
          break;
        case 'Identity_Expression':
          tags.add('identity');
          tags.add('self-expression');
          break;
      }
    }

    // Content type tags
    if (metadata.contentType) {
      tags.add(metadata.contentType.toLowerCase().replace(/_/g, '-'));
    }

    // Adaptation level tags
    if (metadata.adaptationLevel) {
      tags.add(`${metadata.adaptationLevel.toLowerCase()}-adaptation`);
    }

    // Target layer tags
    if (metadata.targetLayers && metadata.targetLayers.length > 0) {
      metadata.targetLayers.forEach(layer => {
        tags.add(`targets-${layer.toLowerCase()}`);
      });
      if (metadata.targetLayers.length > 3) {
        tags.add('multi-layer');
        tags.add('comprehensive');
      }
    }

    // User style tags
    if (metadata.userStyle && metadata.userStyle.length > 0) {
      metadata.userStyle.forEach(style => {
        tags.add(style.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Aesthetic profile tags
    if (metadata.aestheticProfile && metadata.aestheticProfile.length > 0) {
      metadata.aestheticProfile.forEach(aesthetic => {
        tags.add(`${aesthetic.toLowerCase()}-aesthetic`);
      });
    }

    // Personality trait tags
    if (metadata.personalityTraits && metadata.personalityTraits.length > 0) {
      metadata.personalityTraits.forEach(trait => {
        tags.add(trait.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Privacy level tags
    if (metadata.privacyLevel) {
      tags.add(metadata.privacyLevel.toLowerCase());
      switch (metadata.privacyLevel) {
        case 'Public':
          tags.add('open');
          tags.add('shareable');
          break;
        case 'Private':
          tags.add('personal');
          tags.add('secure');
          break;
        case 'Anonymous':
          tags.add('anonymous');
          tags.add('privacy-focused');
          break;
      }
    }

    // Creativity tags
    if (metadata.creativityLevel) {
      if (metadata.creativityLevel > 0.8) {
        tags.add('highly-creative');
        tags.add('innovative');
      } else if (metadata.creativityLevel > 0.6) {
        tags.add('creative');
      } else if (metadata.creativityLevel < 0.3) {
        tags.add('conventional');
        tags.add('traditional');
      }
    }

    // Uniqueness tags
    if (metadata.uniquenessScore) {
      if (metadata.uniquenessScore > 0.8) {
        tags.add('unique');
        tags.add('distinctive');
      } else if (metadata.uniquenessScore < 0.3) {
        tags.add('common');
        tags.add('mainstream');
      }
    }

    // Platform tags
    if (metadata.platform) {
      tags.add(metadata.platform.toLowerCase());
      if (metadata.platform.toLowerCase().includes('mobile')) {
        tags.add('mobile-optimized');
      } else if (metadata.platform.toLowerCase().includes('vr')) {
        tags.add('immersive');
      }
    }

    // Update frequency tags
    if (metadata.updateFrequency) {
      tags.add(metadata.updateFrequency.toLowerCase().replace(/_/g, '-'));
      switch (metadata.updateFrequency) {
        case 'Real_Time':
          tags.add('dynamic');
          tags.add('live');
          break;
        case 'Manual':
          tags.add('user-controlled');
          tags.add('manual');
          break;
      }
    }

    // Engagement tags
    if (metadata.engagementMetrics) {
      if (
        metadata.engagementMetrics.satisfactionScore &&
        metadata.engagementMetrics.satisfactionScore > 0.8
      ) {
        tags.add('high-satisfaction');
        tags.add('well-liked');
      }
      if (
        metadata.engagementMetrics.usageFrequency &&
        metadata.engagementMetrics.usageFrequency > 0.7
      ) {
        tags.add('frequently-used');
        tags.add('popular');
      }
    }

    // Social tags
    if (metadata.shareable) {
      tags.add('shareable');
      tags.add('social');
    }

    if (
      metadata.collaborativeElements &&
      metadata.collaborativeElements.length > 0
    ) {
      tags.add('collaborative');
      tags.add('community-driven');
    }

    // Moderation tags
    if (metadata.moderationStatus) {
      tags.add(metadata.moderationStatus.toLowerCase());
      if (metadata.moderationStatus === 'Approved') {
        tags.add('verified');
        tags.add('safe');
      }
    }

    // Data usage tags
    if (metadata.anonymized) {
      tags.add('anonymized');
      tags.add('privacy-safe');
    }

    // Goal tags
    if (
      metadata.personalizationGoals &&
      metadata.personalizationGoals.length > 0
    ) {
      metadata.personalizationGoals.forEach(goal => {
        tags.add(goal.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Visual preference tags
    if (metadata.visualPreferences) {
      if (metadata.visualPreferences.colorPalette?.length) {
        tags.add('color-aware');
      }
      if (metadata.visualPreferences.stylePreferences?.length) {
        tags.add('style-conscious');
      }
    }

    return Array.from(tags).slice(0, 30);
  }

  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  } {
    const categories = {
      primary: [] as string[],
      secondary: [] as string[],
      descriptive: [] as string[],
    };

    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();

      // Primary tags - type and main function
      if (
        lowerTag.includes('personalize') ||
        lowerTag.includes('customization') ||
        lowerTag.includes('avatar') ||
        lowerTag.includes('style') ||
        lowerTag.includes('behavior') ||
        lowerTag.includes('identity')
      ) {
        categories.primary.push(tag);
      }
      // Secondary tags - characteristics and scope
      else if (
        lowerTag.includes('adaptation') ||
        lowerTag.includes('privacy') ||
        lowerTag.includes('creative') ||
        lowerTag.includes('unique')
      ) {
        categories.secondary.push(tag);
      }
      // Descriptive tags - specific details
      else {
        categories.descriptive.push(tag);
      }
    });

    return categories;
  }

  validateBusinessRules(metadata: PersonalizeLayerMetadata): ValidationResult {
    const errors: any[] = [];
    const warnings: any[] = [];

    // Business rule: Personal name should be meaningful
    if (metadata.personalName && metadata.personalName.trim().length < 3) {
      warnings.push({
        field: 'personalName',
        message: 'Personal name should be at least 3 characters long',
        suggestion: 'Provide a more descriptive personalization name',
      });
    }

    // Business rule: Should have user ID for personalization
    if (!metadata.userId || metadata.userId.trim().length < 3) {
      errors.push({
        field: 'userId',
        type: 'required',
        message: 'User ID is required for personalization',
        severity: 'error',
      });
    }

    // Business rule: Should have base assets or clear goals
    const hasBaseAssets =
      metadata.baseAssets &&
      ((metadata.baseAssets.songAssets &&
        metadata.baseAssets.songAssets.length > 0) ||
        (metadata.baseAssets.starAssets &&
          metadata.baseAssets.starAssets.length > 0) ||
        (metadata.baseAssets.lookAssets &&
          metadata.baseAssets.lookAssets.length > 0) ||
        (metadata.baseAssets.moveAssets &&
          metadata.baseAssets.moveAssets.length > 0) ||
        (metadata.baseAssets.worldAssets &&
          metadata.baseAssets.worldAssets.length > 0) ||
        (metadata.baseAssets.compositeAssets &&
          metadata.baseAssets.compositeAssets.length > 0));

    if (
      !hasBaseAssets &&
      (!metadata.personalizationGoals ||
        metadata.personalizationGoals.length === 0)
    ) {
      warnings.push({
        field: 'baseAssets',
        message:
          'Personalization should have either base assets or clear goals defined',
        suggestion: 'Select base assets or define personalization goals',
      });
    }

    // Business rule: Privacy settings should be appropriate for content
    if (
      metadata.privacyLevel === 'Public' &&
      metadata.personalityTraits &&
      metadata.personalityTraits.length > 0
    ) {
      warnings.push({
        field: 'privacyLevel',
        message:
          'Public personalization with personality traits may raise privacy concerns',
        suggestion: 'Consider using Friends or Private privacy level',
      });
    }

    // Business rule: AI scores should be 0-1
    const aiFields = [
      'creativityLevel',
      'uniquenessScore',
      'appropriatenessScore',
    ];
    aiFields.forEach(field => {
      if (
        metadata[field as keyof PersonalizeLayerMetadata] &&
        ((metadata[field as keyof PersonalizeLayerMetadata] as number) < 0 ||
          (metadata[field as keyof PersonalizeLayerMetadata] as number) > 1)
      ) {
        warnings.push({
          field,
          message: `${field} should be a value between 0 and 1`,
          suggestion: 'Provide a decimal value between 0 and 1',
        });
      }
    });

    // Business rule: Retention period should be reasonable
    if (
      metadata.retentionPeriod &&
      (metadata.retentionPeriod < 1 || metadata.retentionPeriod > 3650)
    ) {
      warnings.push({
        field: 'retentionPeriod',
        message: 'Retention period should be between 1 day and 10 years',
        suggestion: 'Provide a reasonable retention period',
      });
    }

    // Business rule: Content should be moderated for public sharing
    if (metadata.shareable && metadata.moderationStatus !== 'Approved') {
      warnings.push({
        field: 'moderationStatus',
        message: 'Shareable content should be approved through moderation',
        suggestion: 'Complete content moderation before enabling sharing',
      });
    }

    // Business rule: Engagement metrics should be valid
    if (metadata.engagementMetrics) {
      if (
        metadata.engagementMetrics.satisfactionScore &&
        (metadata.engagementMetrics.satisfactionScore < 0 ||
          metadata.engagementMetrics.satisfactionScore > 1)
      ) {
        warnings.push({
          field: 'engagementMetrics.satisfactionScore',
          message: 'Satisfaction score should be between 0 and 1',
          suggestion: 'Provide a valid satisfaction score',
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  getDefaultMetadata(): PersonalizeLayerMetadata {
    return {
      layerType: 'personalize',
      personalName: '',
      personalizationType: 'Experience_Adaptation',
      userId: '',
      description: '',
      userPreferences: [],
      targetLayers: [],
      personalizationGoals: [],
      userStyle: [],
      aestheticProfile: [],
      personalityTraits: [],
      dataUsageConsent: [],
      collaborativeElements: [],
      versionCompatibility: [],
      contentFlags: [],
      adaptationLevel: 'Moderate',
      privacyLevel: 'Private',
      moderationStatus: 'Pending',
      updateFrequency: 'Daily',
      anonymized: true,
      shareable: false,
      baseAssets: {},
      demographicInfo: {},
      visualPreferences: {},
      behavioralPreferences: {},
      engagementMetrics: {},
    };
  }

  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  } {
    return {
      // Personalization is unique to each user
      personalName: 'never',
      userId: 'never',
      baseAssets: 'never',

      // Could inherit preferences from user profiles
      userPreferences: 'merge',
      visualPreferences: 'merge',
      personalizationGoals: 'merge',
    };
  }

  private normalizeArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [];
  }

  private normalizeDate(value: any): Date | undefined {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? undefined : date;
    }
    return undefined;
  }

  private normalizePersonalizationType(
    value: any
  ): PersonalizeLayerMetadata['personalizationType'] {
    if (!value) return 'Experience_Adaptation';

    const normalized = String(value).toLowerCase().replace(/[\s-]/g, '_');

    if (normalized.includes('avatar') || normalized.includes('character'))
      return 'Avatar_Customization';
    if (normalized.includes('style') || normalized.includes('preference'))
      return 'Style_Preference';
    if (normalized.includes('behavior') || normalized.includes('personality'))
      return 'Behavior_Profile';
    if (normalized.includes('content') || normalized.includes('curation'))
      return 'Content_Curation';
    if (normalized.includes('identity') || normalized.includes('expression'))
      return 'Identity_Expression';

    return 'Experience_Adaptation';
  }

  private normalizeContentType(
    value: any
  ): PersonalizeLayerMetadata['contentType'] {
    if (!value) return undefined;

    const normalized = String(value).toLowerCase();

    if (normalized.includes('video')) return 'Video';
    if (normalized.includes('mixed') || normalized.includes('multi'))
      return 'Mixed_Media';

    return 'Image';
  }

  private normalizeAdaptationLevel(
    value: any
  ): PersonalizeLayerMetadata['adaptationLevel'] {
    if (!value) return 'Moderate';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('minimal') || normalized.includes('basic'))
      return 'Minimal';
    if (normalized.includes('extensive') || normalized.includes('high'))
      return 'Extensive';
    if (normalized.includes('complete') || normalized.includes('full'))
      return 'Complete';

    return 'Moderate';
  }

  private normalizePrivacyLevel(
    value: any
  ): PersonalizeLayerMetadata['privacyLevel'] {
    if (!value) return 'Private';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('public')) return 'Public';
    if (normalized.includes('friend')) return 'Friends';
    if (normalized.includes('anonymous')) return 'Anonymous';

    return 'Private';
  }

  private normalizeUpdateFrequency(
    value: any
  ): PersonalizeLayerMetadata['updateFrequency'] {
    if (!value) return 'Daily';

    const normalized = String(value).toLowerCase().replace(/[\s-]/g, '_');

    if (normalized.includes('real') || normalized.includes('live'))
      return 'Real_Time';
    if (normalized.includes('hour')) return 'Hourly';
    if (normalized.includes('week')) return 'Weekly';
    if (normalized.includes('manual')) return 'Manual';

    return 'Daily';
  }

  private normalizeModerationStatus(
    value: any
  ): PersonalizeLayerMetadata['moderationStatus'] {
    if (!value) return 'Pending';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('approved')) return 'Approved';
    if (normalized.includes('flagged')) return 'Flagged';
    if (normalized.includes('rejected')) return 'Rejected';

    return 'Pending';
  }

  private normalizeDemographicInfo(
    value: any
  ): PersonalizeLayerMetadata['demographicInfo'] {
    if (!value) return {};

    return {
      ageRange: value.ageRange || value.age,
      location: value.location || value.region,
      interests: this.normalizeArray(value.interests),
    };
  }

  private normalizeVisualPreferences(
    value: any
  ): PersonalizeLayerMetadata['visualPreferences'] {
    if (!value) return {};

    return {
      colorPalette: this.normalizeArray(value.colorPalette || value.colors),
      stylePreferences: this.normalizeArray(
        value.stylePreferences || value.styles
      ),
      visualThemes: this.normalizeArray(value.visualThemes || value.themes),
    };
  }

  private normalizeBehavioralPreferences(
    value: any
  ): PersonalizeLayerMetadata['behavioralPreferences'] {
    if (!value) return {};

    return {
      interactionStyle: value.interactionStyle,
      pacePreference: value.pacePreference || 'Medium',
      complexityPreference: value.complexityPreference || 'Moderate',
      feedbackStyle: this.normalizeArray(value.feedbackStyle),
    };
  }

  private normalizeEngagementMetrics(
    value: any
  ): PersonalizeLayerMetadata['engagementMetrics'] {
    if (!value) return {};

    return {
      usageFrequency: value.usageFrequency,
      satisfactionScore: value.satisfactionScore,
      adaptationSuccess: value.adaptationSuccess,
    };
  }

  private extractBaseAssets(
    assets: any
  ): PersonalizeLayerMetadata['baseAssets'] {
    if (!assets) return {};

    return {
      songAssets: this.normalizeArray(
        assets.songAssets || assets.songs || assets.G
      ),
      starAssets: this.normalizeArray(
        assets.starAssets || assets.stars || assets.S
      ),
      lookAssets: this.normalizeArray(
        assets.lookAssets || assets.looks || assets.L
      ),
      moveAssets: this.normalizeArray(
        assets.moveAssets || assets.moves || assets.M
      ),
      worldAssets: this.normalizeArray(
        assets.worldAssets || assets.worlds || assets.W
      ),
      compositeAssets: this.normalizeArray(
        assets.compositeAssets || assets.composites || assets.C
      ),
    };
  }
}

// Export singleton instance
export const personalizeLayerStrategy = new PersonalizeLayerStrategy();
